import React, { useEffect, useRef, useState } from 'react'
import { getBreadCrumbs } from '../../../../utils/utils'

import './App.scss'
import { NavigationProps } from './Navigation.types'
import { BreadCrumbs, Menu } from '../data/data.types'
import NavMenuItem from './NavMenuItem'
import { getActiveTab, getSubMenuCount, handleLinkClick } from '../utils/common-utils'
import { ANALYTICS_TYPES } from '../../../SiteNav/util'

export const Navigation = ({
  mainMenu,
  layoutClass,
  textSizeClass,
  menuIconDisable,
  rightButton,
  styles = {
    navBackgroundColor: '#F5F5F5',
    linkTextColor: '#036BB7',
    linkTextColorHover: '#00284a',
    navTabHoverBorderBottom: 'rgba(0, 40, 75, 0.5)',
    navTabHoverBackgroundColor: '#F5F5F5',
    navTabSelectedBorderBottom: 'rgb(0,40,73)',
  },
  enableAnalytics,
}: NavigationProps) => {
  let breadCrumbs: BreadCrumbs | undefined = undefined
  let menuItems
  const [scrollPosition, setScrollPosition] = useState(0)
  const [isSticky, setIsSticky] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const [currentFocusMenu, setCurrentFocusMenu] = useState<number | null>(null)
  const [activeTab] = useState<string>(() => getActiveTab(mainMenu?.data.menuItems))

  const [totalNumberMenuItems] = useState<number>(() => {
    if (!mainMenu?.data?.menuItems) {
      return 0
    }
    return (
      mainMenu.data.menuItems.length +
      mainMenu.data.menuItems.reduce((prev, menu) => {
        return prev + getSubMenuCount(menu)
      }, 0) +
      (rightButton ? 1 : 0)
    )
  })
  const firstNavItemRef = useRef<HTMLAnchorElement>(null)
  const navRef = useRef<HTMLElement>(null)

  if (mainMenu?.data?.menuItems) {
    const { data } = mainMenu
    menuItems = data.menuItems
    breadCrumbs = getBreadCrumbs(menuItems)
    if (menuItems.length) {
      const event = new Event('NavLoaded')
      document.dispatchEvent(event)
    }
  }

  const scrollListener = () => {
    const body = document.getElementsByTagName('body')[0]
    const isLargeViewport = window.innerWidth >= 1025
    const newScrollPosition = window.scrollY
    if (!isScrolling) {
      window.requestAnimationFrame(() => {
        if (isLargeViewport && getComputedStyle(body).overflowY !== 'hidden') {
          setIsSticky(
            scrollPosition > 72 && newScrollPosition <= scrollPosition && newScrollPosition > 72
          )
        }
        setScrollPosition(newScrollPosition)
        setIsScrolling(false)
      })
      setIsScrolling(true)
    }
  }

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase()
    // IE user-agent strings contain "MSIE" and/or "Trident" (code name for IE's rendering engine)
    const _isIE = ua.indexOf('msie') !== -1 || ua.indexOf('trident') !== -1
    if (!_isIE) {
      window.addEventListener('scroll', scrollListener)
    }
    return () => {
      window.removeEventListener('scroll', scrollListener)
    }
  }, [scrollPosition])

  const moveFocusUp = (e: KeyboardEvent, isTab: boolean = false) => {
    if (currentFocusMenu === 0) {
      if (isTab) {
        setCurrentFocusMenu(null)
      }
      return
    }
    e.preventDefault()
    setCurrentFocusMenu(() => (currentFocusMenu ?? totalNumberMenuItems - 1) - 1)
  }
  const moveFocusDown = (e: KeyboardEvent, isTab: boolean = false) => {
    if (currentFocusMenu === totalNumberMenuItems - 1) {
      if (isTab) {
        setCurrentFocusMenu(null)
      }
      return
    }
    e.preventDefault()
    setCurrentFocusMenu(() => (currentFocusMenu ?? 0) + 1)
  }
  const focusKeyboardTrap = (e: KeyboardEvent) => {
    const nav = navRef.current
    if (!nav || !navRef.current?.contains(document.activeElement)) {
      return
    }
    switch (e.key) {
      case 'Tab': {
        if (e.shiftKey) {
          moveFocusUp(e, true)
          return
        }
        moveFocusDown(e, true)
        return
      }
      case 'ArrowDown':
      case 'ArrowRight': {
        moveFocusDown(e)
        return
      }
      case 'ArrowUp':
      case 'ArrowLeft': {
        moveFocusUp(e)
        return
      }
      default: {
        return
      }
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', focusKeyboardTrap)
    return () => {
      window.removeEventListener('keydown', focusKeyboardTrap)
    }
  }, [currentFocusMenu])

  useEffect(() => {
    const mouseTrap = (e: MouseEvent) => {
      const nav = navRef.current
      if (!nav || navRef.current?.contains(e.target as Node) || currentFocusMenu === null) {
        return
      }
      setCurrentFocusMenu(null)
    }
    window.addEventListener('click', mouseTrap)
    return () => {
      window.removeEventListener('click', mouseTrap)
    }
  }, [currentFocusMenu])
  const depthLevel = 0
  let idOffset = 0

  let rightButtonMenu: Menu | null = null
  if (rightButton) {
    rightButtonMenu = {
      HstMenuItemRepresentation: {
        name: rightButton.label,
        url: rightButton.link,
        parameters: {
          breadcrumbReverse: 'false',
          sitemapDisable: 'false',
          breadcrumbDisable: 'false',
          menuDisable: 'false',
          showDropdown: 'false',
          ['data-analytics']: '',
          analyticsSatellite: rightButton.analyticsSatellite,
        },
        menuItems: [],
      },
    }
  }
  const buildBreadcrumbs = () => {
    if (!breadCrumbs) {
      return null
    }
    const dataAnalyticsValue: string[] = []
    const breadCrumbList = breadCrumbs.breadCrumbs.map(breadCrumb => {
      dataAnalyticsValue.push(breadCrumb.pageName)
      return (
        <>
          <li className='u-textSizeMsrm1 List-item u-paddingVert2gu u-paddingHoriz0'>
            <a
              href={breadCrumb.pageURL}
              data-analytics={dataAnalyticsValue.join(' | ')}
              onClick={
                enableAnalytics
                  ? e => {
                      handleLinkClick(
                        e,
                        ANALYTICS_TYPES.BREADCRUMB,
                        dataAnalyticsValue.join(' | '),
                        undefined
                      )
                    }
                  : undefined
              }
              className={`u-textSizeMsrm1 u-linkClean ${
                breadCrumbs?.currentPageParams.breadcrumbReverse === 'true' && 'u-textColorWhite'
              }`}>
              {breadCrumb.pageName}
            </a>
          </li>
          <span
            aria-hidden='true'
            className='Icon u-textSizeMsrm1 List-item u-paddingVert2gu u-paddingHoriz2gu'>
            &#xe5cc;
          </span>
        </>
      )
    })
    dataAnalyticsValue.push(breadCrumbs.currentPage)
    breadCrumbList.push(
      <li
        className='u-textSizeMsrm1 List-item u-paddingVert2gu u-paddingHoriz0'
        aria-current={'page'}>
        <a
          className='u-textColorLight u-linkClean'
          href={breadCrumbs.currentPageUrl}
          onClick={
            enableAnalytics
              ? e => {
                  handleLinkClick(
                    e,
                    ANALYTICS_TYPES.BREADCRUMB,
                    dataAnalyticsValue.join(' | '),
                    undefined
                  )
                }
              : undefined
          }
          data-analytics={dataAnalyticsValue.join(' | ')}>
          {breadCrumbs.currentPage}
        </a>
      </li>
    )
    return breadCrumbList
  }
  return (
    <div
      className={`navigation-bar ${isSticky ? 'nav-sticky u-sizeFull' : ''}`}
      style={{ backgroundColor: styles.navBackgroundColor }}>
      <div
        className={`${layoutClass || 'u-sizeConstrained'} u-sm-hidden u-md-hidden u-posRelative`}>
        <nav className='u-flex u-flexJustifyBetween' ref={navRef}>
          <ul className='menus List List--selectable List--inline u-flexJustifyStart u-decoBorderReset'>
            {menuItems?.map((menu, index) => {
              const totalSubmenus = getSubMenuCount(menuItems[index])
              idOffset += index === 0 ? 0 : getSubMenuCount(menuItems[index - 1])
              return (
                <NavMenuItem
                  item={menu}
                  textSizeClass={textSizeClass}
                  key={menu.HstMenuItemRepresentation.name}
                  depthLevel={depthLevel}
                  index={index + idOffset}
                  styles={styles}
                  activeTab={activeTab}
                  currentFocusMenu={currentFocusMenu}
                  totalSubmenus={totalSubmenus}
                  menuIconDisable={menuIconDisable}
                  firstNavItemRef={index === 0 ? firstNavItemRef : null}
                  updateCurrFocusedIndex={setCurrentFocusMenu}
                  isRightButton={false}
                  enableAnalytics={enableAnalytics}
                />
              )
            })}
          </ul>
          {rightButtonMenu && (
            <ul>
              <NavMenuItem
                item={rightButtonMenu}
                textSizeClass={textSizeClass}
                depthLevel={depthLevel}
                index={totalNumberMenuItems - 1}
                styles={styles}
                activeTab={activeTab}
                currentFocusMenu={currentFocusMenu}
                totalSubmenus={0}
                firstNavItemRef={null}
                updateCurrFocusedIndex={setCurrentFocusMenu}
                isRightButton={true}
                enableAnalytics={enableAnalytics}
              />
            </ul>
          )}
        </nav>
        {breadCrumbs &&
          !isSticky &&
          breadCrumbs.currentPageParams.breadcrumbDisable !== 'true' &&
          breadCrumbs.breadCrumbs.length !== 0 && (
            <nav className='breadcrumbs' aria-label='Breadcrumb'>
              <ul>{buildBreadcrumbs()}</ul>
            </nav>
          )}
      </div>
    </div>
  )
}

export default Navigation
